import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";

type PetTagResponse = {
  id: number;
  nome: string;
  tipo_pet: string | null;
  outro_tipo_pet?: string | null;
  raca: string | null;
  sexo?: string | null;
  foto: string | null;
  microchipado?: boolean;
  microchip_codigo?: string | null;
  castrado?: boolean;
  observacoes: string | null;
  tutor: {
    nome: string | null;
    telefone: string | null;
  };
  saude?: {
    toma_medicacao: boolean;
    medicacao: string | null;
    obs_medicacoes: string | null;
    tem_alergia: boolean;
    alergias: string | null;
    possui_deficiencia: boolean;
    tipo_deficiencia: string | null;
    doencas: string | null;
    alimentacao_especial: boolean;
    descricao_alimentacao: string | null;
  };
  comportamento?: {
    agressivo: boolean;
    sociavel_pets: boolean;
    sociavel_criancas: boolean;
    medo_chuva: boolean;
    medo_trovao_fogos: boolean;
    aceita_outros_pets: boolean;
    precisa_espaco_cercado: boolean;
    precisa_supervisao_constante: boolean;
  };
  rotina?: {
    horario_refeicao: string | null;
    horario_passeio: string | null;
    objetos_essenciais: string | null;
  };
  emergencia?: {
    contato: string | null;
    contato_medico_vet: string | null;
    autoriza_vet: boolean;
    autoriza_plantao: boolean;
  };
};

function onlyDigits(value?: string | null) {
  return (value || "").replace(/\D/g, "");
}

function normalizeBrazilPhone(value?: string | null) {
  const digits = onlyDigits(value);

  if (!digits) return "";
  if (digits.startsWith("55")) return digits;

  return `55${digits}`;
}

function capitalize(value?: string | null) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function InfoCard({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 whitespace-pre-line text-base font-semibold leading-6 text-slate-900">
        {value}
      </div>
    </div>
  );
}

function WarningCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold leading-6 text-amber-950">
      {children}
    </div>
  );
}

export default function PetTagPublica() {
  const { token } = useParams();
  const [pet, setPet] = useState<PetTagResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    async function carregar() {
      try {
        setLoading(true);
        setErro("");

        const resp = await fetch(`/api/pet/tag/${token}`, {
          headers: { Accept: "application/json" },
        });

        if (!resp.ok) {
          throw new Error("Pet não encontrado");
        }

        const data = await resp.json();
        setPet(data);
      } catch {
        setErro("Não foi possível carregar a identificação deste pet.");
      } finally {
        setLoading(false);
      }
    }

    if (token) carregar();
  }, [token]);

  const phone = normalizeBrazilPhone(pet?.tutor?.telefone);

  const whatsappUrl = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(
        `Olá, encontrei o pet ${pet?.nome || ""}. Vi o QR Code da AllePets.`,
      )}`
    : "";

  const telUrl = phone ? `tel:+${phone}` : "";

  const petType = pet?.tipo_pet === "outro" ? pet.outro_tipo_pet : pet?.tipo_pet;
  const headline = [capitalize(petType), pet?.raca, pet?.sexo].filter(Boolean).join(" • ");

  const criticalAlerts = useMemo(() => {
    if (!pet) return [];

    const alerts: string[] = [];

    if (pet.comportamento?.agressivo) {
      alerts.push("Pode reagir com agressividade. Aproxime-se com cuidado e evite movimentos bruscos.");
    }

    if (pet.comportamento?.precisa_supervisao_constante) {
      alerts.push("Precisa de supervisão constante.");
    }

    if (pet.saude?.toma_medicacao && pet.saude.medicacao) {
      alerts.push(`Toma medicação: ${pet.saude.medicacao}`);
    }

    if (pet.saude?.tem_alergia && pet.saude.alergias) {
      alerts.push(`Alergias: ${pet.saude.alergias}`);
    }

    if (pet.saude?.possui_deficiencia && pet.saude.tipo_deficiencia) {
      alerts.push(`Deficiência ou condição especial: ${pet.saude.tipo_deficiencia}`);
    }

    if (pet.comportamento?.medo_trovao_fogos) {
      alerts.push("Pode ter medo de trovões ou fogos.");
    }

    return alerts;
  }, [pet]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="rounded-3xl bg-white px-6 py-5 text-center text-sm font-semibold text-slate-700 shadow-sm">
          Carregando identificação...
        </div>
      </main>
    );
  }

  if (erro || !pet) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md rounded-3xl bg-white p-6 text-center shadow-sm">
          <div className="text-sm font-bold uppercase tracking-wide text-[#FE5668]">
            AllePets
          </div>
          <h1 className="mt-3 text-2xl font-black text-slate-900">Tag não encontrada</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {erro || "Pet não encontrado ou tag inativa."}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 pb-8">
      <section className="bg-[#FE5668] px-5 pb-24 pt-8 text-white">
        <div className="mx-auto max-w-xl text-center">
          <div className="text-xs font-black uppercase tracking-[0.22em] opacity-90">
            Pet encontrado
          </div>
          <h1 className="mt-3 text-4xl font-black leading-tight">Este é {pet.nome}</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm font-medium leading-6 opacity-95">
            Mantenha o pet em segurança e entre em contato com o tutor o quanto antes.
          </p>
        </div>
      </section>

      <section className="mx-auto -mt-16 max-w-xl px-5">
        <div className="overflow-hidden rounded-[2rem] bg-white shadow-xl">
          <img
            src={pet.foto || "/images/placeholders/pet-default.png"}
            alt={pet.nome}
            className="h-72 w-full bg-slate-200 object-cover"
          />

          <div className="space-y-5 p-5">
            <div>
              <h2 className="text-3xl font-black text-slate-950">{pet.nome}</h2>
              {headline && <p className="mt-1 text-base font-medium text-slate-600">{headline}</p>}
            </div>

            {phone ? (
              <div className="grid grid-cols-1 gap-3">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl bg-green-600 px-5 py-4 text-center text-base font-black text-white shadow-sm transition hover:bg-green-700"
                >
                  Chamar tutor no WhatsApp
                </a>
                <a
                  href={telUrl}
                  className="rounded-2xl border border-slate-300 bg-white px-5 py-4 text-center text-base font-black text-slate-950 transition hover:bg-slate-50"
                >
                  Ligar agora
                </a>
              </div>
            ) : (
              <WarningCard>
                O tutor não possui telefone público cadastrado nesta tag. Mantenha o pet em segurança e procure apoio local.
              </WarningCard>
            )}

            {pet.tutor?.nome && <InfoCard label="Tutor" value={pet.tutor.nome} />}

            {pet.microchipado && (
              <InfoCard label="Microchip" value={pet.microchip_codigo || "Pet microchipado"} />
            )}

            {criticalAlerts.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-black uppercase tracking-wide text-slate-700">
                  Atenção antes de manusear
                </h3>
                {criticalAlerts.map((alert) => (
                  <WarningCard key={alert}>{alert}</WarningCard>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <InfoCard label="Observações do tutor" value={pet.observacoes} />
              <InfoCard label="Condição de saúde" value={pet.saude?.doencas} />
              <InfoCard label="Observações sobre medicação" value={pet.saude?.obs_medicacoes} />
              <InfoCard
                label="Alimentação especial"
                value={pet.saude?.alimentacao_especial ? pet.saude?.descricao_alimentacao : null}
              />
              <InfoCard label="Horário de refeição" value={pet.rotina?.horario_refeicao} />
              <InfoCard label="Objetos essenciais" value={pet.rotina?.objetos_essenciais} />
              <InfoCard label="Contato de emergência" value={pet.emergencia?.contato} />
              <InfoCard label="Veterinário ou clínica" value={pet.emergencia?.contato_medico_vet} />
            </div>

            {(pet.emergencia?.autoriza_vet || pet.emergencia?.autoriza_plantao) && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold leading-6 text-emerald-950">
                {pet.emergencia?.autoriza_vet && <div>O tutor autoriza atendimento veterinário.</div>}
                {pet.emergencia?.autoriza_plantao && <div>O tutor autoriza atendimento de plantão.</div>}
              </div>
            )}

            <div className="rounded-2xl bg-slate-100 p-4 text-sm font-medium leading-6 text-slate-700">
              <strong>Orientação:</strong> não solte o pet novamente. Se possível, mantenha-o em local seguro, ofereça água e aguarde contato do tutor.
            </div>
          </div>
        </div>

        <div className="mt-5 text-center text-xs font-semibold text-slate-500">
          Identificação digital gerada pela AllePets.
        </div>
      </section>
    </main>
  );
}
